import { Component, computed, input, OnDestroy, OnInit, output, viewChild } from '@angular/core';
import { Site } from '@app/models/site.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { CUSTOMER_ID, LocalStorageService, SITE, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { UserAccountService } from '@app/services/user-account.service';
import { BrnSheet } from '@spartan-ng/brain/sheet';
import { Subject, takeUntil } from 'rxjs';

export interface TitleBarViewModel {
    title: string;
    subtitle?: string;
    showAccountSection?: boolean;
    showBackButton?: boolean;
}

@Component({
    selector: 'app-title-bar',
    templateUrl: './title-bar.component.html',
    styleUrls: ['./title-bar.component.css'],
    standalone: false,
})
export class TitleBarComponent implements OnInit, OnDestroy {
    public customerID: string | undefined;
    public userAccountFeatureEnabled = false;
    public userLoggedIn: boolean = false;
    public ngUnsubscribe: Subject<any> = new Subject();
    public readonly sheetRef = viewChild<BrnSheet>('sheet');
    public readonly goBack = output<void>();
    public readonly viewModel = input<TitleBarViewModel | undefined>({ title: 'Title' });

    public showAccountContainer = computed(() => this.viewModel()?.showAccountSection === true && this.userAccountFeatureEnabled);

    constructor(
        private localStorageService: LocalStorageService,
        private featureFlagService: FeatureFlagService,
        private userAccountService: UserAccountService
    ) {}

    ngOnInit() {
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.customerID = this.localStorageService.getString(CUSTOMER_ID);
        this.userAccountFeatureEnabled =
            this.featureFlagService.getFeatureFlag(FeatureKey.UserAccounts) && siteBookingSettings?.allowClientOnlineAccountAccess === true;
        if (this.userAccountFeatureEnabled) {
            this.userLoggedIn = this.userAccountService.getCurrentUser() !== undefined;
        }

        this.userAccountService.userAccountUpdated$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((userAccount) => {
            this.userLoggedIn = userAccount !== undefined;
        });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.complete();
    }

    public onClickLogIn(): void {
        this.sheetRef()?.open();
    }

    public onCloseSheet(): void {
        this.sheetRef()?.close();
    }

    public onClickAccount(): void {
        const site = this.localStorageService.get(SITE) as Site;
        let url = site?.bookingURL + '/User#overview';
        if (!url.startsWith('http')) {
            url = 'https://' + url;
        }
        if (this.isEmbedded()) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = url;
        }
    }

    private isEmbedded(): boolean {
        try {
            return window.self !== window.top;
        } catch {
            return true;
        }
    }

    public onGoBack(): void {
        this.goBack.emit();
    }
}
